'use client';

import React from 'react';
import { m } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { chip, selection } from 'styled-system/recipes';

import { useDesignMotion } from '@shaojiang/components/motion';
import type { Text } from '@shaojiang/content';

import { Copy } from './Copy';

interface FilterChipsProps<T extends string> {
  options: { value: T; label: Text }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  /** Distinguishes the sliding marker when a page has two groups */
  id: string;
  className?: string;
}

const styles = selection();

/** Filter pills; the "on" fill slides to the chosen one. */
export function FilterChips<T extends string>({
  options,
  value,
  onChange,
  label,
  id,
  className,
}: FilterChipsProps<T>) {
  const motion = useDesignMotion();
  return (
    <div
      role='group'
      aria-label={label}
      className={cx(css({ display: 'flex', gap: '2', flexWrap: 'wrap' }), className)}
    >
      {options.map((option) => {
        const on = option.value === value;
        return (
          <button
            key={option.value}
            type='button'
            aria-pressed={on}
            onClick={() => onChange(option.value)}
            className={cx(chip(), styles.control)}
          >
            {on && (
              <m.span
                aria-hidden='true'
                layoutId={`chip-${id}`}
                className={styles.indicator}
                transition={motion.layout}
              />
            )}
            <Copy text={option.label} />
          </button>
        );
      })}
    </div>
  );
}
